import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button, Tag, message, Modal, Tooltip, Dropdown, Menu, Icon, Spin } from 'antd';
import { FixedSizeList } from 'react-window';
import moment from 'moment';
import 'moment/locale/pt-br';

import Layout from '~/components/UI/Layout/Layout';
import { ModuleHeader } from '~/components/ModuleHeader';
import Search from '~/_shared/components/Search';
import { PERMISSIONS } from '~/constants/permissions.constants';

import { listarCampanhas, removerCampanha, baixarRelatorioPdf, duplicarCampanha, cancelarCampanha } from './api';
import { statusLabel, statusColor, SOURCE_LABEL, taxa } from './helpers';

// Uma grade só, usada pelo cabeçalho e por cada linha — é o que mantém as
// colunas alinhadas mesmo com as linhas vindo de uma lista virtualizada, que
// não compartilha <table> com o cabeçalho.
const GRADE = '1fr 130px 150px 130px 160px 96px';
const ALTURA_LINHA = 72;

const Celula = ({ children, align, style }) => (
  <div style={{
    display: 'flex', flexDirection: 'column', justifyContent: 'center',
    alignItems: align === 'right' ? 'flex-end' : 'flex-start',
    minWidth: 0, ...style,
  }}>
    {children}
  </div>
);

const Numero = ({ valor, legenda, cor }) => (
  <div style={{ textAlign: 'right', lineHeight: 1.25 }}>
    <div style={{ fontSize: 16, fontWeight: 600, color: cor || '#1f2430', fontVariantNumeric: 'tabular-nums' }}>
      {valor}
    </div>
    {legenda && <div style={{ fontSize: 11, color: '#8c8c8c' }}>{legenda}</div>}
  </div>
);

const Vazio = ({ podeEditar }) => (
  <div style={{ textAlign: 'center', padding: '56px 20px' }}>
    <div style={{
      width: 56, height: 56, borderRadius: 16, background: '#f0f5ff',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14,
    }}>
      <Icon type="mail" style={{ fontSize: 24, color: '#1890ff' }} />
    </div>
    <div style={{ fontSize: 16, fontWeight: 500, color: '#1f2430' }}>Nenhuma campanha ainda</div>
    <div style={{ fontSize: 13, color: '#8c8c8c', marginTop: 4, maxWidth: 420, margin: '4px auto 0' }}>
      Uma campanha envia um e-mail para um recorte da sua base de clientes —
      por regime, atividade, estado ou situação.
    </div>
    {podeEditar && (
      <Link to="/marketing/campanhas/nova">
        <Button type="primary" icon="plus" style={{ marginTop: 18, borderRadius: 8 }}>
          Criar a primeira
        </Button>
      </Link>
    )}
  </div>
);

const ScreensCampanhas = ({ history }) => {
  const [campanhas, setCampanhas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const user = useSelector((state) => state.user);

  const isAdmin = !!(user && user.id) && user.permissions_group_id === 1;
  const permissoes = (user && user.permissions) || [];
  const pode = (chave) => isAdmin || permissoes.indexOf(chave) !== -1;
  const podeEditar = pode(PERMISSIONS.marketing.campaigns.edit);

  const carregar = useCallback(() => {
    setLoading(true);
    listarCampanhas()
      .then((dados) => setCampanhas(dados || []))
      .catch(() => message.error('Não foi possível carregar as campanhas.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const confirmarRemocao = (campanha) => {
    Modal.confirm({
      title: 'Remover esta campanha?',
      content: campanha.status === 'sent'
        ? 'A campanha já foi enviada. O histórico de entrega sai da lista junto.'
        : 'O rascunho será removido.',
      okText: 'Remover',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: () => removerCampanha(campanha.id).then(carregar),
    });
  };

  const filtradas = useMemo(() => {
    const alvo = busca.toLowerCase();
    return campanhas.filter((c) =>
      (c.name || '').toLowerCase().indexOf(alvo) >= 0 ||
      (c.subject || '').toLowerCase().indexOf(alvo) >= 0
    );
  }, [campanhas, busca]);

  // O PDF sai por XHR: a rota exige Bearer e X-Tenant, cabeçalhos que um <a>
  // não manda — link direto voltaria 401.
  const baixarPdf = (c) => {
    const fecharAviso = message.loading('Gerando o PDF…', 0);
    baixarRelatorioPdf(c.id)
      .then((blob) => {
        if (!blob) return;
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'campanha-' + c.id + '.pdf';
        link.click();
        URL.revokeObjectURL(link.href);
      })
      .catch(() => message.error('Não foi possível gerar o PDF.'))
      .finally(fecharAviso);
  };

  const duplicar = (c) =>
    duplicarCampanha(c.id)
      .then((nova) => {
        message.success('Cópia criada como rascunho.');
        if (nova && nova.id) history.push('/marketing/campanhas/' + nova.id);
        else carregar();
      })
      .catch(() => message.error('Não foi possível duplicar.'));

  const confirmarCancelamento = (c) => {
    Modal.confirm({
      title: 'Cancelar o envio desta campanha?',
      content: (
        <div>
          <p style={{ marginBottom: 6 }}>
            Quem ainda está na fila não recebe. <strong>O que já saiu não volta</strong> —
            e-mail entregue não se recolhe.
          </p>
          <p style={{ marginBottom: 0, color: '#8c8c8c' }}>
            Até aqui: {c.enviados} de {c.publico} já foram enviados.
          </p>
        </div>
      ),
      okText: 'Cancelar o envio',
      okType: 'danger',
      cancelText: 'Voltar',
      onOk: () => cancelarCampanha(c.id).then(carregar).catch(() => message.error('Não foi possível cancelar.')),
    });
  };

  const menuDaLinha = (c, semRelatorio) => (
    <Menu style={{ minWidth: 190, borderRadius: 8 }}>
      <Menu.Item key="abrir">
        <Link to={'/marketing/campanhas/' + c.id + (semRelatorio ? '' : '/relatorio')}>
          <Icon type={semRelatorio ? 'edit' : 'bar-chart'} style={{ marginRight: 8 }} />
          {semRelatorio ? 'Continuar editando' : 'Ver relatório'}
        </Link>
      </Menu.Item>

      {!semRelatorio && (
        <Menu.Item key="pdf" onClick={() => baixarPdf(c)}>
          <Icon type="file-pdf" style={{ marginRight: 8 }} />
          Baixar PDF
        </Menu.Item>
      )}

      {podeEditar && (
        <Menu.Item key="duplicar" onClick={() => duplicar(c)}>
          <Icon type="copy" style={{ marginRight: 8 }} />
          Duplicar
        </Menu.Item>
      )}

      {podeEditar && (c.status === 'sending' || c.status === 'scheduled') && (
        <Menu.Item key="cancelar" onClick={() => confirmarCancelamento(c)}>
          <span style={{ color: '#d46b08' }}>
            <Icon type="stop" style={{ marginRight: 8 }} />
            {c.status === 'scheduled' ? 'Cancelar agendamento' : 'Cancelar envio'}
          </span>
        </Menu.Item>
      )}

      {podeEditar && c.status !== 'sending' && <Menu.Divider />}
      {podeEditar && c.status !== 'sending' && (
        <Menu.Item key="remover" onClick={() => confirmarRemocao(c)}>
          <span style={{ color: '#cf1322' }}>
            <Icon type="delete" style={{ marginRight: 8 }} />
            Remover
          </span>
        </Menu.Item>
      )}
    </Menu>
  );

  const Linha = ({ index, style }) => {
    const c = filtradas[index];
    const rascunho = c.status === 'draft';
    // Agendada também não tem relatório nem PDF: nada foi enviado ainda.
    const semRelatorio = rascunho || c.status === 'scheduled';

    return (
      <div
        style={{
          ...style,
          display: 'grid', gridTemplateColumns: GRADE, gap: 12,
          alignItems: 'center', padding: '0 18px',
          borderBottom: '1px solid #f0f0f0', background: '#fff',
        }}
      >
        <Celula>
          <Link
            to={'/marketing/campanhas/' + c.id + (semRelatorio ? '' : '/relatorio')}
            style={{ fontWeight: 500, fontSize: 14.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}
          >
            {c.name}
          </Link>
          <div style={{ fontSize: 12, color: '#8c8c8c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
            {c.subject || <i>sem assunto</i>}
            {c.source ? ' · ' + SOURCE_LABEL[c.source] : ''}
          </div>
        </Celula>

        <Celula>
          <Tag color={statusColor(c.status)} style={{ margin: 0 }}>{statusLabel(c.status)}</Tag>
        </Celula>

        <Celula align="right">
          {semRelatorio ? <span style={{ color: '#d9d9d9' }}>—</span> : (
            <Tooltip title={(c.nao_enviados || 0) + ' cliente(s) do filtro ficaram de fora, com o motivo no relatório'}>
              <div>
                <Numero
                  valor={c.publico}
                  legenda={c.no_filtro ? 'de ' + c.no_filtro + ' no filtro' : null}
                />
              </div>
            </Tooltip>
          )}
        </Celula>

        <Celula align="right">
          {semRelatorio ? <span style={{ color: '#d9d9d9' }}>—</span> : (
            <Numero
              valor={c.enviados}
              legenda={c.falhas
                ? c.falhas + (c.falhas > 1 ? ' falharam' : ' falhou')
                : taxa(c.enviados, c.publico)}
              cor={c.enviados ? '#237804' : '#8c8c8c'}
            />
          )}
        </Celula>

        <Celula>
          {c.sent_at ? (
            <Tooltip title={moment(c.sent_at).format('DD/MM/YYYY [às] HH:mm')}>
              <span style={{ fontSize: 13 }}>{moment(c.sent_at).fromNow()}</span>
            </Tooltip>
          ) : c.scheduled_at ? (
            <Tooltip title="Disparo agendado">
              <span style={{ fontSize: 13, color: '#0958d9' }}>
                <Icon type="clock-circle" style={{ marginRight: 4 }} />
                {moment(c.scheduled_at).format('DD/MM [às] HH:mm')}
              </span>
            </Tooltip>
          ) : (
            <span style={{ fontSize: 13, color: '#bfbfbf' }}>
              criada {moment(c.created_at).fromNow()}
            </span>
          )}
        </Celula>

        <Celula align="right">
          {/* Um menu em vez de dois ícones soltos: ícone sem rótulo obriga a
              adivinhar, e a lista ainda vai ganhar duplicar e cancelar. */}
          <Dropdown overlay={menuDaLinha(c, semRelatorio)} trigger={['click']} placement="bottomRight">
            <Button size="small" icon="more" style={{ border: 'none', boxShadow: 'none' }} />
          </Dropdown>
        </Celula>
      </div>
    );
  };

  // As duas ações do módulo cabem num menu só. O rótulo mora no item, não no
  // botão: um "+" sozinho diria "criar", e descadastros não é criar nada.
  const menuDeAcoes = (
    <Menu style={{ minWidth: 250, borderRadius: 8, padding: 4 }}>
      {podeEditar && (
        <Menu.Item key="nova" style={{ padding: 0 }}>
          <Link to="/marketing/campanhas/nova" style={{ display: 'block', padding: '9px 12px' }}>
            <Icon type="mail" style={{ color: '#1890ff', marginRight: 10 }} />
            <span style={{ fontWeight: 500 }}>Nova campanha</span>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginLeft: 26, lineHeight: 1.4 }}>
              Escolher o público e escrever o e-mail
            </div>
          </Link>
        </Menu.Item>
      )}
      {podeEditar && <Menu.Divider />}
      <Menu.Item key="descadastros" style={{ padding: 0 }}>
        <Link to="/marketing/campanhas/descadastros" style={{ display: 'block', padding: '9px 12px' }}>
          <Icon type="stop" style={{ color: '#8c8c8c', marginRight: 10 }} />
          <span style={{ fontWeight: 500 }}>Descadastros</span>
          <div style={{ fontSize: 12, color: '#8c8c8c', marginLeft: 26, lineHeight: 1.4 }}>
            Quem pediu para sair — e como devolver à lista
          </div>
        </Link>
      </Menu.Item>
    </Menu>
  );

  const cabecalho = (
    <div style={{
      display: 'grid', gridTemplateColumns: GRADE, gap: 12, padding: '10px 18px',
      background: '#fafafa', borderBottom: '1px solid #e8e8e8',
      fontSize: 11, fontWeight: 600, letterSpacing: '.05em',
      textTransform: 'uppercase', color: '#8c8c8c',
    }}>
      <div>Campanha</div>
      <div>Situação</div>
      <div style={{ textAlign: 'right' }}>Público</div>
      <div style={{ textAlign: 'right' }}>Enviados</div>
      <div>Quando</div>
      <div />
    </div>
  );

  // Altura da janela virtual: acompanha o conteúdo até um teto, para a lista
  // não empurrar a página inteira quando houver muitas campanhas.
  const alturaLista = Math.min(filtradas.length * ALTURA_LINHA, 560);

  return (
    <Layout>
      <ModuleHeader
        breadcrumbs={['Marketing', 'Campanhas']}
        title="Campanhas"
        search={
          <Search
            value={busca}
            placeholder="Buscar por nome ou assunto"
            onChange={(e) => setBusca(e.target.value)}
          />
        }
        actions={
          <Dropdown overlay={menuDeAcoes} trigger={['click']} placement="bottomRight">
            <Button
              type="primary"
              icon="plus"
              shape="circle"
              size="large"
              title="Ações"
              style={{ boxShadow: '0 2px 8px rgba(24,144,255,.35)' }}
            />
          </Dropdown>
        }
      />

      <div style={{
        background: '#fff', border: '1px solid #e8e8e8', borderRadius: 10,
        overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
        ) : filtradas.length === 0 ? (
          <Vazio podeEditar={podeEditar && campanhas.length === 0} />
        ) : (
          <div>
            {cabecalho}
            <FixedSizeList
              height={alturaLista}
              itemCount={filtradas.length}
              itemSize={ALTURA_LINHA}
              width="100%"
            >
              {Linha}
            </FixedSizeList>
          </div>
        )}
      </div>

      {!loading && filtradas.length > 0 && (
        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 10 }}>
          {filtradas.length} campanha{filtradas.length === 1 ? '' : 's'}
          {busca && ' encontrada' + (filtradas.length === 1 ? '' : 's')}
          {' · '}
          <span>“Público” é quem podia receber; quem ficou de fora aparece no relatório com o motivo.</span>
        </div>
      )}
    </Layout>
  );
};

export default ScreensCampanhas;
